import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Grid, Text, useBreakpoint, Message, List } from 'basis';
import { useTheme as emotionUseTheme } from '@emotion/react';
import { LayoutPage, Form, Errors, FormRenderer, InternalLink, OfferTile } from 'components';
import { OverviewContent } from 'siteContent/overviewContent';
import { useStoreValue, APOLLO_NETWORK_ERROR, APOLLO_UNKNOWN_ERROR } from 'store';
import { submitHandler, getDataLayerElements } from 'utils';
import { useEventTracking } from 'react-event-tracker';
import { APPLY_BTN_LABEL } from '_config/_constants';
import GemNzBanner from 'images/banner/gem-nz-banner.webp';
import GemNzBannerMobile from 'images/banner/gem-nz-banner-mobile.webp';
import GemNzBackground from 'images/banner/gem-nz-background.webp';
import { config, PRODUCT_CODE_NZ_GEM, setConfig, STEP_CONTACT_DETAILS } from '_config';
import { COUNTRY_CODE_NZ } from '_config/countryCodes';
import { OverviewSources } from '_config/_constants/externalLinks';
import { useInitApplication, useErrorTrackingAndUpdateStore, useFormInitialValues, useValidateOffers } from 'hooks';
import { kebabCase } from 'lodash';
import { LandingSubHeader } from 'components/merchant/LandingSubHeader';
import { isFeatureOn } from 'featureToggles';
import { FeaturesAndBenefitsModal } from './FeaturesAndBenefitsModal';
import { HeaderBannerTextArea } from './HeaderBannerTextArea';
import { useAdblockDetector } from '../../hooks/useAdblockDetector';
import {
  Banner,
  BannerImage,
  BannerLeft,
  BannerRight,
  ContentWrapper,
  IconTitleWrapper,
} from './LandingPageGemNz.styles';
import { StartApplicationButton } from './StartApplicationButton';
import { ApplicationOverviewGemVisaNZ } from './ApplicationOverviewGemVisaNZ';
import { DEV_FEATURES } from '../../__dev';

const { formHeader } = OverviewContent[config.countryCode];
const overviewSources = OverviewSources[config.countryCode];

const FORM_ID = 'overview';

const formTealiumLocation = kebabCase(formHeader);
const pageTealiumLocation = 'what-to-expect';
const internalLinkEventConfig = {
  category: 'text-link',
  action: 'internal-link',
  location: formTealiumLocation,
};

export function LandingPageGemNz() {
  const theme = emotionUseTheme();
  const breakpoint = useBreakpoint();
  const history = useHistory();
  const [featureAndBenefitsModalOpen, setFeatureAndBenefitsModalOpen] = useState(false);
  const [storeState, updateStore] = useStoreValue();
  const { trackEvent } = useEventTracking();
  const { handleErrorCallback } = useErrorTrackingAndUpdateStore();
  const initialValues = useFormInitialValues(FORM_ID);
  const [isAdblockCheckComplete, isAdblockerDetected] = useAdblockDetector(
    `//tags.tiqcdn.com/utag/latitudefs/apply-funnel/${import.meta.env.VITE_TEALIUM_ENV}/utag.sync.js`,
  );

  const trackApplicationReasonChange = useCallback(
    ({ selectedOption: { value, label } }) => {
      trackEvent({
        event: {
          category: 'application',
          location: formTealiumLocation,
          action: 'interaction',
          label,
        },
        ...getDataLayerElements(storeState),
      });
    },
    [storeState, trackEvent],
  );

  const formConfig = useMemo(
    () =>
      config.generateOverviewFormConfig({
        TermAndConditions,
        StopperMessage,
        trackApplicationReasonChange,
      }),
    [trackApplicationReasonChange],
  );

  useEffect(() => {
    setConfig({
      countryCode: COUNTRY_CODE_NZ,
      productCode: PRODUCT_CODE_NZ_GEM,
    });
  }, []);

  useEffect(() => {
    if (isFeatureOn('balanceTransfer') && retrievePromoCode(storeState.applyAndBuy)) {
      validateOffer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitSuccess = data => {
    const updates = {
      applicationRef: data.applicationRef,
      applicationId: data.applicationId,
      isResuming: false,
    };

    trackEvent({
      event: {
        category: 'application',
        action: 'application-started',
        location: formTealiumLocation,
        label: APPLY_BTN_LABEL,
      },
      ...getDataLayerElements({
        ...storeState,
        ...updates,
      }),
    });

    updateStore({
      applicationErrors: null,
      activeStep: STEP_CONTACT_DETAILS,
      ...updates,
    });

    history.push(STEP_CONTACT_DETAILS);
  };

  const { submit, loading } = useInitApplication({
    onSuccess: handleSubmitSuccess,
    onError: handleErrorCallback,
  });

  const { validateOffer, retrievePromoCode } = useValidateOffers({
    onError: handleErrorCallback,
  });

  const onSubmit = ({ values, errors }) => {
    function initialiseApplication() {
      const applicationStartDate = new Date().toISOString();
      const newAcquisition = {
        ...storeState.acquisition,
        overview: { ...values },
      };
      updateStore({ applicationStartDate, acquisition: newAcquisition });
      submit(values);
    }
    submitHandler({ submit: initialiseApplication, errors });
  };

  const retrySubmit = () => {
    submit(storeState.acquisition.overview);
  };

  const openFeatureAndBenefitsModal = () => {
    setFeatureAndBenefitsModalOpen(true);
    trackEvent({
      event: {
        category: 'text-link',
        action: 'interaction',
        location: pageTealiumLocation,
        label: 'View features and benefits',
      },
      ...getDataLayerElements(storeState),
    });
  };

  const closeFeatureAndBenefitsModal = () => {
    setFeatureAndBenefitsModalOpen(false);
  };

  if (storeState.applicationErrors) {
    const retry = [APOLLO_NETWORK_ERROR, APOLLO_UNKNOWN_ERROR].includes(storeState.applicationErrors.type)
      ? retrySubmit
      : null;
    return (
      <LayoutPage hideBackButton noChildrenPadding>
        <Errors retry={retry} retrying={loading} isPreSubmission />
      </LayoutPage>
    );
  }

  return (
    <LayoutPage noSessionTimeout={!storeState.acquisition.overview.consentGiven} hideBackButton noChildrenPadding>
      <FeaturesAndBenefitsModal isOpen={featureAndBenefitsModalOpen} onClose={closeFeatureAndBenefitsModal} />
      <LandingSubHeader {...storeState.applyAndBuy} />
      <Banner>
        <Container width="100%" padding="0" padding-lg="0">
          <BannerLeft theme={theme}>
            <img src={GemNzBackground} alt="" aria-hidden="true" />
          </BannerLeft>
          <BannerRight theme={theme}>
            <HeaderBannerTextArea openFeatureAndBenefitsModal={openFeatureAndBenefitsModal} />
            <BannerImage theme={theme}>
              <img src={['md', 'lg', 'xl'].includes(breakpoint) ? GemNzBanner : GemNzBannerMobile} alt="LFrame card" />
            </BannerImage>
          </BannerRight>
        </Container>
      </Banner>
      <Container padding="0 5 5 5" paddingMd="0">
        <Form id={FORM_ID} initialValues={initialValues} onSubmit={onSubmit} hideFormButtons disableFormCache>
          {formData => {
            return (
              <ContentWrapper theme={theme}>
                <Container bg="white" margin="auto">
                  <Grid cols="10" colsGap="8">
                    <Grid.Item colSpan="all" colSpan-md="0-3">
                      <OfferTile />
                      <ApplicationOverviewGemVisaNZ pinkStyling={false} />
                    </Grid.Item>
                    <Grid.Item colSpan="all" colSpan-md="4-10">
                      <IconTitleWrapper theme={theme}>
                        <Text as="h2" textStyle="heading4">
                          {formHeader}
                        </Text>
                      </IconTitleWrapper>
                      <Grid rowsGap="4">
                        <FormRenderer config={formConfig} formData={formData} />
                      </Grid>
                      <Container>
                        <Text textStyle="heading5" as="h4">
                          <Container margin="6 0" width-md="100%">
                            <StartApplicationButton
                              backgroundColor={theme.newStageButtonColor}
                              disabled={
                                isAdblockCheckComplete && isAdblockerDetected && !DEV_FEATURES.OVERRIDE_AD_BLOCKER_STOP
                              }
                              loading={loading}
                              theme={theme}
                            >
                              {APPLY_BTN_LABEL}
                            </StartApplicationButton>
                          </Container>
                        </Text>
                      </Container>
                      {isAdblockCheckComplete && isAdblockerDetected && !DEV_FEATURES.OVERRIDE_AD_BLOCKER_STOP && (
                        <Message severity="stop" bg="secondary.pink.t30">
                          Please turn off your adblocker for the application to work and refresh your browser once it is
                          done.
                        </Message>
                      )}
                    </Grid.Item>
                  </Grid>
                </Container>
              </ContentWrapper>
            );
          }}
        </Form>
      </Container>
    </LayoutPage>
  );
}

function TermAndConditions() {
  return (
    <>
      <Text textStyle="heading6" bold="semiBold">
        By starting this application:
      </Text>
      <br />
      <List>
        <List.Item>
          I confirm that I meet the eligibility criteria and I understand I am applying for a general-purpose credit
          card which I require on an ongoing basis
        </List.Item>
        <List.Item>
          I confirm that I have read, understood and I agree to the{' '}
          <InternalLink
            label={overviewSources.privacy_acknowledgement_and_authorisation.label}
            url={overviewSources.privacy_acknowledgement_and_authorisation.url}
            eventConfig={internalLinkEventConfig}
            newTab
          />
          {', '}
          <InternalLink
            label={overviewSources.gem_visa_conditions_of_use.label}
            url={overviewSources.gem_visa_conditions_of_use.url}
            eventConfig={internalLinkEventConfig}
            newTab
          />
          {', '}and the generic product{' '}
          <InternalLink
            label={overviewSources.initial_disclosure_statement.label}
            url={overviewSources.initial_disclosure_statement.url}
            eventConfig={internalLinkEventConfig}
            newTab
          />
          .
        </List.Item>
        <List.Item>I acknowledge that, if approved, I will be sent a Gem Visa credit card in the mail.</List.Item>
        <List.Item>
          I agree to electronically receiving my credit contract as set out in the Initial Disclosure Statement and the
          Gem Visa Conditions of Use.
        </List.Item>
      </List>
    </>
  );
}

function StopperMessage() {
  return (
    <>
      Please be advised this may not be the right product for you. You can{' '}
      <InternalLink
        label={overviewSources.visit_our_website.label}
        url={overviewSources.visit_our_website.url}
        eventConfig={internalLinkEventConfig}
        newTab
      />{' '}
      to decide which product suits your needs best.
    </>
  );
}
